import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newAnn, setNewAnn] = useState({ title: '', content: '' });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const showMsg = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await API.get('/announcements');
      setAnnouncements(res.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newAnn.title || !newAnn.content) {
      showMsg('Please fill in all fields!', 'error');
      return;
    }
    try {
      await API.post('/announcements', newAnn);
      showMsg('✅ Announcement posted!', 'success');
      setShowAdd(false);
      setNewAnn({ title: '', content: '' });
      fetchAnnouncements();
    } catch (error) {
      showMsg('Error posting announcement!', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await API.delete(`/announcements/${id}`);
      showMsg('✅ Announcement deleted!', 'success');
      fetchAnnouncements();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📢 Announcements</h2>
          <p style={styles.subtitle}>
            Post announcements to all members (max 5 at a time)
          </p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowAdd(true)}>
          + New Announcement
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={messageType === 'success' ? styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* Info Box */}
      <div style={styles.infoBox}>
        <p style={styles.infoText}>
          💡 Only the latest 5 announcements are shown to members.
          The oldest one is automatically removed when you post a new one.
        </p>
      </div>

      {/* Announcements List */}
      {loading ? (
        <p style={styles.loadingText}>Loading...</p>
      ) : announcements.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyIcon}>📢</p>
          <p style={styles.emptyText}>No announcements yet!</p>
          <p style={styles.emptySubText}>
            Click "New Announcement" to post one.
          </p>
        </div>
      ) : (
        <div style={styles.annList}>
          {announcements.map((ann, index) => (
            <div key={ann.id} style={styles.annCard}>
              <div style={styles.annLeft}>
                <div style={styles.annNumber}>
                  {announcements.length - index}
                </div>
                <div style={styles.annInfo}>
                  <h3 style={styles.annTitle}>{ann.title}</h3>
                  <p style={styles.annContent}>{ann.content}</p>
                  <p style={styles.annMeta}>
                    Posted by {ann.first_name} {ann.last_name} ·{' '}
                    {new Date(ann.created_at).toLocaleDateString('en-PH', {
                      year: 'numeric', month: 'long', day: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              <button style={styles.deleteBtn}
                onClick={() => handleDelete(ann.id)}>
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>📢 New Announcement</h3>
            <div style={styles.field}>
              <label style={styles.label}>Title *</label>
              <input style={styles.input} type="text"
                placeholder="ex. Practice cancelled tomorrow"
                value={newAnn.title}
                onChange={e => setNewAnn({
                  ...newAnn, title: e.target.value
                })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Message *</label>
              <textarea
                style={{ ...styles.input, height: '120px', resize: 'vertical' }}
                placeholder="Write your announcement here..."
                value={newAnn.content}
                onChange={e => setNewAnn({
                  ...newAnn, content: e.target.value
                })} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowAdd(false)}>
                Cancel
              </button>
              <button style={styles.doneBtn} onClick={handleAdd}>
                Post Announcement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '20px',
    flexWrap: 'wrap', gap: '10px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  addBtn: {
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
  },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  infoBox: {
    background: '#e8f0fe', borderRadius: '10px',
    padding: '12px 16px', marginBottom: '20px',
  },
  infoText: { margin: 0, color: '#0f3460', fontSize: '13px' },
  loadingText: { textAlign: 'center', color: '#888', padding: '30px' },
  emptyState: { textAlign: 'center', padding: '50px 0' },
  emptyIcon: { fontSize: '50px', margin: '0 0 10px 0' },
  emptyText: { color: '#555', fontSize: '16px', margin: '0 0 5px 0' },
  emptySubText: { color: '#aaa', fontSize: '13px', margin: 0 },
  annList: { display: 'flex', flexDirection: 'column', gap: '15px' },
  annCard: {
    background: 'white', borderRadius: '14px',
    padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', gap: '15px',
  },
  annLeft: { display: 'flex', gap: '15px', flex: 1 },
  annNumber: {
    width: '36px', height: '36px', borderRadius: '50%',
    background: '#0f3460', color: 'white',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 'bold',
    fontSize: '16px', flexShrink: 0,
  },
  annInfo: { flex: 1 },
  annTitle: {
    margin: '0 0 8px 0', fontSize: '16px',
    fontWeight: 'bold', color: '#333',
  },
  annContent: {
    margin: '0 0 10px 0', color: '#555',
    fontSize: '14px', lineHeight: '1.5',
  },
  annMeta: { margin: 0, color: '#aaa', fontSize: '12px' },
  deleteBtn: {
    background: 'none', border: '1px solid #ddd',
    borderRadius: '8px', padding: '6px 10px',
    cursor: 'pointer', fontSize: '16px', flexShrink: 0,
  },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1001,
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '16px',
    padding: '30px', width: '100%', maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: {
    margin: '0 0 20px 0', fontSize: '18px',
    fontWeight: 'bold', color: '#333',
  },
  field: { marginBottom: '15px' },
  label: {
    display: 'block', fontSize: '13px',
    fontWeight: '600', color: '#444', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px',
    borderRadius: '10px', border: '1.5px solid #ddd',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '20px' },
  cancelBtn: {
    flex: 1, padding: '12px', background: 'white',
    color: '#666', border: '1.5px solid #ddd',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '14px',
  },
  doneBtn: {
    flex: 1, padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
  },
};

export default Announcements;